import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { type ComponentProps, useState } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { EmptyState } from '../../components/common/EmptyState';
import { CardSkeleton } from '../../components/common/Skeleton';
import { LoanForm } from '../../components/loans/LoanForm';
import { LoansList } from '../../components/loans/LoansList';
import { trpc } from '../../lib/trpc';
import { usePageTitle } from '../../lib/usePageTitle';

type EditableLoan = ComponentProps<typeof LoanForm>['loan'];
type EditableLoanInput = NonNullable<EditableLoan>;

const searchSchema = z.object({
  loan: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/loans/')({
  validateSearch: searchSchema,
  component: LoansPage,
});

function LoansPage() {
  usePageTitle('Lån');

  const search = useSearch({ from: '/loans/' });
  const navigate = useNavigate();
  const selectedLoanId = search.loan ?? null;

  const [formOpen, setFormOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState<EditableLoan>(null);

  const listQuery = trpc.loan.list.useQuery({}, { retry: false });

  const setSelectedLoan = (loanId: string | null) => {
    void navigate({
      from: '/loans/',
      search: (prev) => ({
        ...prev,
        loan: loanId ?? undefined,
      }),
      replace: true,
    });
  };

  const openCreate = () => {
    setEditingLoan(null);
    setFormOpen(true);
  };

  const openEdit = (loan: EditableLoanInput) => {
    setEditingLoan(loan);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingLoan(null);
  };

  if (listQuery.error?.data?.code === 'NOT_FOUND' || listQuery.error?.data?.code === 'FORBIDDEN') {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Du tillhör inget hushåll ännu.</p>
        <Button type="button" onClick={() => void navigate({ to: '/household/create' })}>
          Skapa hushåll
        </Button>
      </div>
    );
  }

  if (listQuery.error) {
    return <div className="text-destructive text-sm">{listQuery.error.message}</div>;
  }

  const loans = listQuery.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Lån</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Amorteringar och räntor, med prognos för nästa betalning.
          </p>
        </div>
        {loans.length > 0 && (
          <Button type="button" className="shrink-0" onClick={openCreate}>
            Nytt lån
          </Button>
        )}
      </header>

      {listQuery.isLoading ? (
        <CardSkeleton lines={6} />
      ) : loans.length === 0 ? (
        <EmptyState
          icon="🏦"
          title="Inga lån"
          description="Skapa ett lån och koppla regler för att börja följa amorteringar över tid."
          action={{
            label: 'Skapa lån',
            onClick: openCreate,
          }}
        />
      ) : (
        <LoansList
          loans={loans}
          selectedLoanId={selectedLoanId}
          onSelectLoan={setSelectedLoan}
          onCreate={openCreate}
          onEdit={openEdit}
        />
      )}

      <LoanForm open={formOpen} loan={editingLoan} onClose={closeForm} onSaved={closeForm} />
    </div>
  );
}
